import type { ScanResponse } from "./contracts";

export type EvidenceReason = "country_agrees" | "asn_agrees" | "organization_agrees" | "country_differs" | "asn_differs" | "registration_differs" | "proxy_differs" | "partial_sources" | "single_source" | "ownership_not_usage";
export type EvidenceLevel = "high" | "medium" | "low" | "insufficient";
export interface EvidenceAssessment {
  confidence: EvidenceLevel;
  origin: "highly_consistent" | "consistent" | "some_disagreement" | "insufficient";
  reasons: EvidenceReason[];
  disagreements: EvidenceReason[];
}
const orgKey = (value: string) => value.toLowerCase().replace(/[^\p{L}\p{N}]/gu, "");

/** Identity evidence only. No fraud scores, browser traits, or platform probabilities. */
export function assessEvidence(scan: ScanResponse): EvidenceAssessment {
  const reasons: EvidenceReason[] = [];
  const disagreements: EvidenceReason[] = [];
  const countries = [scan.network.countryCode, scan.ipinfo?.countryCode, scan.ip2location?.countryCode].filter((x): x is string => !!x);
  const asns = [scan.network.asn?.toString(), scan.ipinfo?.asnNumber?.replace(/^AS/i, "")].filter((x): x is string => !!x);
  const countryAgrees = countries.length >= 2 && new Set(countries).size === 1;
  const asnAgrees = asns.length >= 2 && new Set(asns).size === 1;
  if (countryAgrees) reasons.push("country_agrees");
  if (asnAgrees) reasons.push("asn_agrees");
  if (countries.length >= 2 && new Set(countries).size > 1) disagreements.push("country_differs");
  if (asns.length >= 2 && new Set(asns).size > 1) disagreements.push("asn_differs");
  const organizationAgrees = !!scan.registration?.organization && !!scan.network.organization &&
    orgKey(scan.registration.organization) === orgKey(scan.network.organization);
  if (organizationAgrees) reasons.push("organization_agrees");
  if (scan.registration?.countryCode && scan.network.countryCode && scan.registration.countryCode !== scan.network.countryCode)
    disagreements.push("registration_differs");
  if (typeof scan.reputation?.proxy === "boolean" && typeof scan.ip2location?.isProxy === "boolean" && scan.reputation.proxy !== scan.ip2location.isProxy)
    disagreements.push("proxy_differs");
  if (scan.reputation?.proxy === true && scan.network.organization) reasons.push("ownership_not_usage");
  const partial = scan.limitations.some((x) => /unavailable|timeout|authentication|invalid_response|upstream/.test(x));
  if (partial) reasons.push("partial_sources");
  // Registration agreement can support Medium, but is not independent geolocation.
  const registrationAgrees = !!scan.registration?.countryCode && scan.registration.countryCode === scan.network.countryCode;
  const identityComparison = countryAgrees || asnAgrees || organizationAgrees || registrationAgrees;
  const identityConflict = disagreements.some((x) => x !== "proxy_differs");
  let confidence: EvidenceLevel = "insufficient";
  if (identityConflict || disagreements.includes("proxy_differs")) confidence = "low";
  else if (identityComparison) confidence = countryAgrees && asnAgrees && organizationAgrees && !partial ? "high" : "medium";
  if (!identityComparison && !identityConflict) reasons.push("single_source");
  return { confidence, origin: identityConflict ? "some_disagreement" : confidence === "high" ? "highly_consistent" : identityComparison ? "consistent" : "insufficient", reasons, disagreements };
}

export function connectionTelemetry(network: ScanResponse["network"]): { transport?: "TCP" | "QUIC / HTTP/3"; rtt?: number } {
  if (network.httpProtocol === "HTTP/3") return { transport: "QUIC / HTTP/3", rtt: network.clientQuicRtt };
  if (network.httpProtocol === "HTTP/1.0" || network.httpProtocol === "HTTP/1.1" || network.httpProtocol === "HTTP/2")
    return { transport: "TCP", rtt: network.clientTcpRtt };
  return {};
}
